/**
 * Landing Hero — Office Meeting preset (FLYR-629).
 *
 * A growth curve climbs left to right across the photo in shallow waves, fading in
 * from the left and sprinkling coins as it goes. Its leading dot is the payoff: it
 * swells into a white disc and stamps a check while the last of the curve is still
 * drawing. Ported from Amy's approved prototype (b2b-header.html).
 *
 * Decorative only (the canvas is aria-hidden); the photo and navy overlay are the
 * no-JS fallback. heroCanvas.js owns the lifecycle: section sizing, retina scaling,
 * the desktop-only gate, the reduced-motion still frame, and teardown.
 *
 * Registered by _blocks/landingHeroPresets/office-meeting.twig, so a page only
 * downloads the animation belonging to the preset it renders.
 */
import {
    drawCheck,
    drawCoins,
    drawTrail,
    fadeInStroke,
    loadCheckIcon,
    mountLandingHeroCanvas,
    sprinkleCoins,
} from './heroCanvas.js';

// The comp draws the curve over ~285 frames, then holds the arrived state for two
// seconds. More coins than the other presets, spread over a longer, flatter path.
const TRAIL_SPEED = 0.0035;
const HOLD_FRAMES = 120;
const NUM_COINS = 30;

// The trail, its coins, and the leading dot all fade in over this first run.
const TRAIL_FADE_END = 0.15;

// The disc starts swelling before the curve finishes, so the climb and the payoff
// overlap rather than reading as two beats.
const DISC_TRIGGER = 0.95;
const DISC_SPEED = 0.03;
const DISC_RADIUS_MIN = 5;
const DISC_RADIUS_MAX = 31.5;
const CHECK_SIZE = 25.5;

mountLandingHeroCanvas('office-meeting', (ctx, { repaintIfStatic }) => {
    const checkIcon = loadCheckIcon(repaintIfStatic);

    let pathAt = () => ({ x: 0, y: 0 });
    let coins = [];
    let progress = 0;
    let discProgress = 0;
    let holdFrames = 0;

    // A steady climb from lower left to upper right with 2.25 sine waves riding it,
    // which is what gives the comp its up-down-up-down-up rhythm.
    function buildPath(width, height) {
        const WAVES = 2.25;
        const amplitude = height * 0.08;
        return (t) => {
            const x = width * (0.5 + (0.84 - 0.5) * t);
            const baseY = height * (0.8 + (0.4 - 0.8) * t);
            return { x, y: baseY - Math.sin(t * Math.PI * 2 * WAVES) * amplitude };
        };
    }

    // The leading dot, which becomes the badge: present the whole way along, swelling
    // once the curve is nearly done.
    function drawLeadingDisc() {
        const point = pathAt(progress);
        const radius = DISC_RADIUS_MIN + (DISC_RADIUS_MAX - DISC_RADIUS_MIN) * discProgress;
        const alpha = Math.min(progress / TRAIL_FADE_END, 1);

        ctx.save();
        ctx.shadowBlur = 15 * discProgress;
        ctx.shadowColor = `rgba(0, 0, 0, ${0.3 * discProgress})`;
        ctx.beginPath();
        ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
        ctx.restore();

        // The check fades and scales in over the disc's second half.
        if (discProgress <= 0.5) {
            return;
        }
        ctx.save();
        ctx.translate(point.x, point.y);
        ctx.globalAlpha = (discProgress - 0.5) / 0.5;
        drawCheck(ctx, checkIcon, CHECK_SIZE * discProgress);
        ctx.restore();
    }

    return {
        resize(width, height) {
            pathAt = buildPath(width, height);
            coins = sprinkleCoins(NUM_COINS);
        },

        step() {
            if (progress < 1) {
                progress = Math.min(progress + TRAIL_SPEED, 1);
            }
            if (progress <= DISC_TRIGGER) {
                return;
            }
            if (discProgress < 1) {
                discProgress = Math.min(discProgress + DISC_SPEED, 1);
                return;
            }
            if (progress < 1) {
                return;
            }
            holdFrames++;
            if (holdFrames > HOLD_FRAMES) {
                progress = 0;
                discProgress = 0;
                holdFrames = 0;
                coins = sprinkleCoins(NUM_COINS);
            }
        },

        // The still frame is the arrived one: curve complete, check stamped.
        finish() {
            progress = 1;
            discProgress = 1;
            holdFrames = 0;
        },

        render() {
            if (progress <= 0) {
                return;
            }
            drawTrail(ctx, pathAt, progress, fadeInStroke(ctx, pathAt, TRAIL_FADE_END));
            drawCoins(ctx, coins, pathAt, progress, TRAIL_FADE_END);
            drawLeadingDisc();
        },
    };
});
